import { useState, useEffect, useCallback } from "react";
import { Select, Checkbox, Button, message, Typography, Space, Alert } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import DataTable from "../../../components/common/DataTable";
import FilterBar from "../../../components/common/FilterBar";
import PageLoader from "../../../components/common/PageLoader";
import usePermission from "../../../hooks/usePermission";
import {
  listAdminUsers,
  getAdminUser,
  updateAccessControl,
} from "../../../services/adminUsers.service";
import { isSuperAdmin, canEditAccessControl } from "./userRules";

const { Text } = Typography;

const VIEW_LIST = "list";
const VIEW_EDIT = "edit";

const MODULES = [
  { key: "dashboard", label: "Dashboard", desc: "Overview & analytics" },
  { key: "deliveryPartners", label: "Delivery Partners", desc: "Rider management" },
  { key: "transactions", label: "Transactions", desc: "Data entry & reconciliation" },
  { key: "cashCOD", label: "Cash & COD", desc: "Deposit approvals" },
  { key: "payrollExpenses", label: "Payroll & Expenses", desc: "Salary, NOC, EMI" },
  { key: "payouts", label: "Payouts", desc: "Payout processing" },
  { key: "usersLeaves", label: "Users & Leaves", desc: "Admin users & leave mgmt" },
  { key: "accounts", label: "Accounts", desc: "Company finances" },
  { key: "communications", label: "Communications", desc: "Broadcast & chat" },
  { key: "settings", label: "Settings", desc: "System configuration" },
];

const PERMISSION_LEVELS = [
  { label: "Full Access", value: "full" },
  { label: "View Only", value: "viewOnly" },
  { label: "Edit", value: "edit" },
  { label: "Approve / Reject", value: "approveReject" },
  { label: "No Access", value: "noAccess" },
];

function levelToFlags(level) {
  switch (level) {
    case "full":
      return { view: true, edit: true, delete: true, approve: true };
    case "viewOnly":
      return { view: true, edit: false, delete: false, approve: false };
    case "edit":
      return { view: true, edit: true, delete: false, approve: false };
    case "approveReject":
      return { view: true, edit: true, delete: false, approve: true };
    case "noAccess":
      return { view: false, edit: false, delete: false, approve: false };
    default:
      return { view: false, edit: false, delete: false, approve: false };
  }
}

function flagsToLevel(flags) {
  if (!flags) return "noAccess";
  const { view, edit, approve } = flags;
  const del = flags.delete;
  if (view && edit && del && approve) return "full";
  if (view && !edit && !del && !approve) return "viewOnly";
  if (view && edit && !del && approve) return "approveReject";
  if (view && edit && !del && !approve) return "edit";
  if (!view && !edit && !del && !approve) return "noAccess";
  return null;
}

function getAccessSummary(user) {
  if (isSuperAdmin(user)) return "Full Access (All Modules)";
  const perms = user.permissions || {};
  const viewCount = MODULES.filter((m) => perms[m.key]?.view).length;
  return `${viewCount} / ${MODULES.length} modules`;
}

export default function AccessControlTab() {
  const { canEdit } = usePermission("usersLeaves");

  const [view, setView] = useState(VIEW_LIST);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [listSearch, setListSearch] = useState("");

  const isReadOnly = editingUser ? !canEditAccessControl(editingUser) : false;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listAdminUsers({ page, per_page: pageSize, search: listSearch });
      setUsers(result.records || []);
      setTotalCount(result.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, listSearch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = async (record) => {
    setEditingUser(record);
    setView(VIEW_EDIT);
    try {
      const user = await getAdminUser(record._id);
      setPermissions(user.permissions || {});
    } catch {
      message.error("Failed to load access control");
    }
  };

  const handleBackToList = () => {
    setView(VIEW_LIST);
    setEditingUser(null);
    setPermissions({});
  };

  const handleLevelChange = (moduleKey, level) => {
    const flags = levelToFlags(level);
    setPermissions((prev) => ({
      ...prev,
      [moduleKey]: { ...flags },
    }));
  };

  const handleCheckboxChange = (moduleKey, action, checked) => {
    setPermissions((prev) => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [action]: checked,
      },
    }));
  };

  const handleSave = async () => {
    if (!editingUser?._id || isReadOnly) return;
    setSaving(true);
    try {
      await updateAccessControl(editingUser._id, permissions);
      message.success("Access control updated. Changes apply on user's next login.");
      await fetchUsers();
      handleBackToList();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading && users.length === 0) {
    return <PageLoader tip="Loading access control..." />;
  }

  if (view === VIEW_LIST) {
    const columns = [
      {
        title: "Name",
        key: "name",
        render: (_, record) => (
          <Text strong style={{ fontSize: 13 }}>
            {record.firstName} {record.lastName}
          </Text>
        ),
      },
      { title: "Email", dataIndex: "email" },
      { title: "Role", dataIndex: "role", width: 80 },
      {
        title: "Access Summary",
        key: "accessSummary",
        render: (_, record) => getAccessSummary(record),
      },
    ];

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <Text strong style={{ fontSize: 16 }}>Access Control</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>Configure module-level permissions</Text>
          </div>
        </div>

        <FilterBar
          searchPlaceholder="Search by name or email..."
          onSearch={(val) => { setListSearch(val); setPage(1); }}
        />

        <DataTable
          columns={columns}
          dataSource={users}
          loading={loading}
          pagination={{ current: page, pageSize, total: totalCount }}
          onPageChange={(p) => setPage(p)}
          actions={{
            onEdit: handleEdit,
            canEdit: true,
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>Access Control</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {editingUser
              ? `Editing: ${editingUser.firstName} ${editingUser.lastName}`
              : "Configure module-level permissions"}
          </Text>
        </div>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBackToList}>
            Back to Listing
          </Button>
          {canEdit && !isReadOnly && (
            <Button type="primary" onClick={handleSave} loading={saving}>
              Save Changes
            </Button>
          )}
        </Space>
      </div>

      {isReadOnly && (
        <Alert
          type="info"
          showIcon
          message="Super Admin permissions are fixed"
          description="Super Admin (L1) users always have full access to all modules. These permissions cannot be changed."
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
              <th style={thStyle}>Module</th>
              <th style={{ ...thStyle, width: 160 }}>Permission Level</th>
              <th style={{ ...thStyle, width: 80, textAlign: "center" }}>View</th>
              <th style={{ ...thStyle, width: 80, textAlign: "center" }}>Edit</th>
              <th style={{ ...thStyle, width: 80, textAlign: "center" }}>Delete</th>
              <th style={{ ...thStyle, width: 90, textAlign: "center" }}>Approve</th>
            </tr>
          </thead>
          <tbody>
            {MODULES.map((mod) => {
              const perms = permissions[mod.key] || {};
              const level = flagsToLevel(perms);

              return (
                <tr key={mod.key} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: "#0f172a" }}>{mod.label}</div>
                    <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>{mod.desc}</div>
                  </td>
                  <td style={tdStyle}>
                    <Select
                      size="small"
                      value={level}
                      disabled={isReadOnly}
                      onChange={(val) => handleLevelChange(mod.key, val)}
                      options={PERMISSION_LEVELS}
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <Checkbox
                      checked={!!perms.view}
                      disabled={isReadOnly}
                      onChange={(e) => handleCheckboxChange(mod.key, "view", e.target.checked)}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <Checkbox
                      checked={!!perms.edit}
                      disabled={isReadOnly}
                      onChange={(e) => handleCheckboxChange(mod.key, "edit", e.target.checked)}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <Checkbox
                      checked={!!perms.delete}
                      disabled={isReadOnly}
                      onChange={(e) => handleCheckboxChange(mod.key, "delete", e.target.checked)}
                    />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <Checkbox
                      checked={!!perms.approve}
                      disabled={isReadOnly}
                      onChange={(e) => handleCheckboxChange(mod.key, "approve", e.target.checked)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ padding: "12px 20px", fontSize: 11, color: "#9ca3af", borderTop: "1px solid #f0f0f0" }}>
          {isReadOnly
            ? "Super Admin access is view-only."
            : "Changes will apply on the user's next login."}
        </div>
      </div>
    </div>
  );
}

const thStyle = {
  padding: "12px 16px",
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#64748b",
  textAlign: "left",
};

const tdStyle = {
  padding: "14px 16px",
  verticalAlign: "middle",
};
